/**
 * scope-shared — building blocks reused by every "scope picker" inside
 * the composer popover family (June 2026).
 *
 * Why split here:
 *   - ``SourceScopePicker`` (folders + files unified) and the deprecated
 *     ``FolderPicker`` (folders only) shared 5 nearly-identical
 *     subcomponents: search box, row, skeleton, empty state, provider
 *     label map. Drift between the two was guaranteed every time we
 *     touched ARIA/keyboard semantics.
 *   - Future scope pickers (connectors, tags, …) can plug into the same
 *     visual contract without re-implementing the row anatomy.
 *
 * The exports are deliberately internal to ``@ando/ui`` — they live in
 * the same folder as the consumers and are NOT re-exported through the
 * package barrel. Consumers outside the composer compound should reach
 * for ``Popover`` + ``Command`` primitives instead.
 */

import { Check, Search, X } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";

// ─── Provider label map ───────────────────────────────────────────────────

export type ScopeFolderProvider =
  | "google_drive"
  | "onedrive"
  | "sharepoint"
  | "filesystem";

export const SCOPE_PROVIDER_LABEL: Record<ScopeFolderProvider, string> = {
  google_drive: "Google",
  onedrive: "OneDrive",
  sharepoint: "SharePoint",
  filesystem: "Locale",
};

/**
 * Expanded source label map for documents — covers Drives + manual +
 * email/chat attachments + AI-generated content. Returns ``null`` for
 * unknown / non-displayable slugs (the row simply omits the badge).
 * White-label invariant: keys mirror the BE ``provider`` field; values
 * are the localised labels the user actually reads in the chip.
 */
const SCOPE_DOC_SOURCE_LABEL: Record<string, string> = {
  google_drive: "Google",
  onedrive: "OneDrive",
  sharepoint: "SharePoint",
  notion: "Notion",
  filesystem: "Locale",
  manual_upload: "Upload",
  email_attachment: "Email",
  chat_attachment: "Chat",
  ai_generated: "AI",
  whatsapp: "WhatsApp",
};

export function scopeDocSourceLabel(provider: string | undefined): string | null {
  if (!provider) return null;
  return SCOPE_DOC_SOURCE_LABEL[provider] ?? null;
}

// ─── SearchBox ────────────────────────────────────────────────────────────

export interface ScopeSearchBoxProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly ariaLabel: string;
  readonly placeholder?: string;
}

export const ScopeSearchBox = React.forwardRef<
  HTMLInputElement,
  ScopeSearchBoxProps
>(function ScopeSearchBox(
  { value, onChange, ariaLabel, placeholder = "Cerca…" },
  ref,
) {
  return (
    <div className="flex items-center gap-2 border-hairline border-b px-3 py-2">
      <Search aria-hidden className="h-3.5 w-3.5 text-ink-3" />
      <input
        ref={ref}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="w-full bg-transparent text-[12px] text-ink-1 placeholder-ink-3 outline-none"
      />
      {value.length > 0 ? (
        <button
          type="button"
          aria-label="Pulisci ricerca"
          onClick={() => onChange("")}
          className="rounded-md p-0.5 text-ink-3 transition-colors hover:bg-ink-1/[0.06] hover:text-ink-1"
        >
          <X aria-hidden className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
});

// ─── Row ──────────────────────────────────────────────────────────────────

export interface ScopeRowProps {
  readonly checked: boolean;
  readonly onToggle: () => void;
  readonly icon: React.ReactElement;
  readonly primary: string;
  readonly secondary?: string;
  readonly badge?: string;
  readonly ariaLabel: string;
}

export function ScopeRow({
  checked,
  onToggle,
  icon,
  primary,
  secondary,
  badge,
  ariaLabel,
}: ScopeRowProps): React.ReactElement {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2 px-3 py-1.5 transition-colors",
        "hover:bg-ink-1/[0.04]",
      )}
    >
      <span
        className={cn(
          "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-all duration-100",
          checked
            ? "scale-100 border-ando-orange bg-ando-orange text-white"
            : "scale-95 border-hairline bg-white",
        )}
        aria-hidden
      >
        <Check
          className={cn(
            "h-2.5 w-2.5 transition-opacity duration-100",
            checked ? "opacity-100" : "opacity-0",
          )}
        />
      </span>
      <input
        type="checkbox"
        aria-label={ariaLabel}
        checked={checked}
        onChange={onToggle}
        className="sr-only"
      />
      {icon}
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span className="truncate text-[12px] text-ink-1">{primary}</span>
        {secondary ? (
          <span className="truncate text-[10px] text-ink-3">{secondary}</span>
        ) : null}
      </span>
      {badge ? (
        <span className="shrink-0 rounded-md bg-ink-1/[0.06] px-1.5 py-px font-medium text-[9px] text-ink-2 uppercase tracking-wide">
          {badge}
        </span>
      ) : null}
    </label>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────

const SCOPE_SKELETON_KEYS = ["a", "b", "c", "d", "e"] as const;

export function ScopePickerSkeleton(): React.ReactElement {
  return (
    <div className="px-3 py-1" aria-hidden>
      {SCOPE_SKELETON_KEYS.map((k) => (
        <div key={k} className="flex items-center gap-2 py-1.5">
          <span className="h-3.5 w-3.5 rounded border border-hairline bg-ink-1/[0.04]" />
          <span className="h-3.5 w-3.5 rounded bg-ink-1/[0.04]" />
          <span className="h-3 flex-1 rounded bg-ink-1/[0.04]" />
          <span className="h-3 w-12 rounded bg-ink-1/[0.04]" />
        </div>
      ))}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────

export interface ScopePickerEmptyProps {
  readonly title: string;
  readonly subtitle: string;
  readonly onOpenIntegrations?: () => void;
}

export function ScopePickerEmpty({
  title,
  subtitle,
  onOpenIntegrations,
}: ScopePickerEmptyProps): React.ReactElement {
  return (
    <div className="px-3 py-4 text-center">
      <p className="text-[12px] text-ink-2">{title}</p>
      <p className="mt-1 text-[11px] text-ink-3">{subtitle}</p>
      {onOpenIntegrations ? (
        <button
          type="button"
          onClick={onOpenIntegrations}
          className="mt-2 rounded-md border border-hairline bg-white px-2.5 py-1 font-medium text-[11px] text-ink-1 transition-colors hover:bg-ink-1/[0.04]"
        >
          Vai a Integrazioni
        </button>
      ) : null}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────
//
// Visual divider between "Selezionati" and "Risultati" lists in the
// document tab — pattern shipped by Linear / Slack / Notion @-mention.

export interface ScopeSectionHeaderProps {
  readonly label: string;
  readonly count?: number;
}

export function ScopeSectionHeader({
  label,
  count,
}: ScopeSectionHeaderProps): React.ReactElement {
  return (
    <li
      role="presentation"
      className="px-3 pt-2 pb-1 font-semibold text-[10px] text-ink-3 uppercase tracking-wider"
    >
      {label}
      {count !== undefined ? (
        <span aria-hidden className="ml-1 text-ink-3/80">
          ({count})
        </span>
      ) : null}
    </li>
  );
}

// ─── 0-results message with optional clear-filter CTA ─────────────────────

export interface ScopeNoResultsProps {
  readonly query: string;
  readonly onClearFilter?: () => void;
  readonly fallbackHint?: string;
}

export function ScopeNoResults({
  query,
  onClearFilter,
  fallbackHint,
}: ScopeNoResultsProps): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="px-3 py-3 text-[11px] text-ink-3"
    >
      {query.length === 0 ? (
        fallbackHint ?? "Digita per cercare…"
      ) : (
        <>
          <p>Nessun risultato per "{query}".</p>
          {onClearFilter ? (
            <button
              type="button"
              onClick={onClearFilter}
              className="mt-1 text-ando-orange transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
            >
              Pulisci filtro
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
